import { createAction, props } from '@ngrx/store';
import { id } from '@wonder-core-ng/interfaces/id.type';
import { PaginatedListDto } from '../interfaces/paginated-list.dto';
import { IActionExecution } from '../event-store/interfaces/event.interface';
import { CreateDashboardBlockDTO, IDashboardBlock } from './interfaces/dashboard-block.interface';
import { CreateDashboardDTO, DateRangeQuery, IDashboard } from './interfaces/dashboard.interface';

export const loadDashboards = createAction('[Dashboard] Load Dashboards');

export const loadDashboardsSuccess = createAction(
  '[Dashboard] Load Dashboards Success',
  props<{ data: PaginatedListDto<IDashboard> }>()
);

export const loadDashboardsFailure = createAction(
  '[Dashboard] Load Dashboards Failure',
  props<{ error: unknown }>()
);

export const loadDashboard = createAction(
  '[Dashboard] Load Dashboard',
  props<{ dashboardId: id }>()
);

export const loadDashboardSuccess = createAction(
  '[Dashboard] Load Dashboard Success',
  props<{ dashboard: IDashboard }>()
);

export const loadDashboardFailure = createAction(
  '[Dashboard] Load Dashboard Failure',
  props<{ error: unknown }>()
);

export const createDashboard = createAction(
  '[Dashboard] Create Dashboard',
  props<{ dashboard: CreateDashboardDTO }>()
);

export const createDashboardSuccess = createAction(
  '[Dashboard] Create Dashboard Success',
  props<{ dashboard: IDashboard }>()
);

export const createDashboardFailure = createAction(
  '[Dashboard] Create Dashboard Failure',
  props<{ error: unknown }>()
);

export const updateDashboard = createAction(
  '[Dashboard] Update Dashboard',
  props<{ dashboard: IDashboard }>()
);

export const updateDashboardSuccess = createAction(
  '[Dashboard] Update Dashboard Success',
  props<{ dashboard: IDashboard }>()
);

export const updateDashboardFailure = createAction(
  '[Dashboard] Update Dashboard Failure',
  props<{ error: unknown }>()
);

export const deleteDashboard = createAction(
  '[Dashboard] Delete Dashboard',
  props<{ dashboardId: id }>()
);

export const deleteDashboardSuccess = createAction('[Dashboard] Delete Dashboard Success');

export const deleteDashboardFailure = createAction(
  '[Dashboard] Delete Dashboard Failure',
  props<{ error: unknown }>()
);

export const selectDashboard = createAction(
  '[Dashboard] Select Dashboard',
  props<{ dashboardId: id }>()
);

export const sortDashboardBlocks = createAction(
  '[Dashboard] Sort Dashboard Blocks',
  props<{ dashboardId: id; blocks: IDashboardBlock[] }>()
);

export const createDashboardBlock = createAction(
  '[Dashboard] Create Dashboard Block',
  props<{ dashboardId: id; block: CreateDashboardBlockDTO }>()
);

export const updateDashboardBlock = createAction(
  '[Dashboard] Update Dashboard Block',
  props<{ dashboardId: id; block: IDashboardBlock }>()
);

export const deleteDashboardBlock = createAction(
  '[Dashboard] Delete Dashboard Block',
  props<{ dashboardId: id; blockId: id }>()
);

export const addChartToBlock = createAction(
  '[Dashboard] Add Chart to Block',
  props<{ chartId: id }>()
);

export const selectEditingBlockId = createAction(
  '[Dashboard] Select editing block id',
  props<{ id: id }>()
);

/**
 * Executions of every action belonging to any action list on this block.
 *
 * Keyed by block rather than by action list because that is how the backend scopes the endpoint: a
 * block is the placement unit and can hold more than one list.
 */
export const loadBlockActionExecutions = createAction(
  '[Dashboard] Load Block Action Executions',
  props<{ blockId: id }>()
);

export const loadBlockActionExecutionsSuccess = createAction(
  '[Dashboard] Load Block Action Executions Success',
  props<{ blockId: id; executions: IActionExecution[] }>()
);

export const loadBlockActionExecutionsFailure = createAction(
  '[Dashboard] Load Block Action Executions Failure',
  props<{ blockId: id; error: unknown }>()
);

/** Marks a `SEND_NOTIFICATION` execution as read. `blockId` travels along so the reducer knows
 *  which block's `blockActionExecutions` slice to update. */
export const markActionExecutionRead = createAction(
  '[Dashboard] Mark Action Execution Read',
  props<{ blockId: id; executionId: number }>()
);

export const markActionExecutionReadSuccess = createAction(
  '[Dashboard] Mark Action Execution Read Success',
  props<{ blockId: id; execution: IActionExecution }>()
);

export const markActionExecutionReadFailure = createAction(
  '[Dashboard] Mark Action Execution Read Failure',
  props<{ blockId: id; executionId: number; error: unknown }>()
);

export const httpResult = createAction(
  '[Application] HTTP Result',
  props<{ action: unknown; error?: unknown }>()
);

export const updateDateRangeQuery = createAction(
  '[Dashboard] Date range update',
  props<{ dateRange: DateRangeQuery | null }>()
);

/** Set the runtime value of a dashboard-wide (grouped) filter, keyed by its source field. */
export const setGlobalFilterValue = createAction(
  '[Dashboard] Set global filter value',
  props<{ key: string; value: unknown }>()
);

export * as DashboardActions from './dashboard.actions';
